import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./EmployeeTime.css";
import PullToRefresh from './PullToRefresh';

const API = {
  EMPLOYEE: "/employee/",
  WORKTIME_STATUS: "/worktime/status/",
  CLOCK_IN: "/worktime/clock_in/",
  CHECK_OVERTIME: "/worktime/check_overtime/",
  CLOCK_OUT: "/worktime/clock_out/",
};

const MESSAGES = {
  LOGIN_EXPIRED: "ログインの有効期限が切れました。再度ログインしてください。",
  ERROR_GENERIC: "エラーが発生しました",
  NETWORK_ERROR: "ネットワークエラーが発生しました。接続を確認してください。",
  NO_SCHEDULE_TODAY:
    "本日はスケジュールが登録されていません。打刻するにはスケジュールアイコンでスケジュールを登録してください。",
};

const STATUS = {
  WORKING: "勤務中",
  LEFT: "退社済み",
  UNCONFIRMED: "未確認",
};

const parseTodayHm = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(String(hhmm).trim())) return null;
  const [h, m] = String(hhmm).trim().split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

const formatNowHm = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const isLateForClockIn = (scheduleStartHm) => {
  const sched = parseTodayHm(scheduleStartHm);
  if (!sched) return false;
  return Date.now() > sched.getTime();
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const hasLateApplicationToday = async (apiClient) => {
  try {
    const res = await apiClient.get("/api/employee/punch-applications/");
    const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
    const today = todayIso();
    return list.some((item) => {
      if (item.category !== "LATE_IN") return false;
      if (item.status === "REJECTED" || item.status === "CANCELLED") return false;
      const fdDate = item.late_date || item.date || "";
      const submittedDay = String(item.submittedAt || "").slice(0, 10);
      return fdDate === today || submittedDay === today;
    });
  } catch {
    return false;
  }
};

/** 打刻忘れリマインド（一覧取得・日別勤怠・cron 等で自動送信されるお知らせのタイトル） */
const PUNCH_REMINDER_TITLE = "打刻のお願い";

const EmployeeTime = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState(null);
  const [message, setMessage] = useState("");
  /** メッセージに添付するリンク情報 { text, to } | null */
  const [messageLink, setMessageLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [statusMessage, setStatusMessage] = useState(STATUS.UNCONFIRMED);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  /** 未読の「打刻のお願い」があれば打刻画面に表示（社員証★の通知一覧にも出る） */
  const [punchReminderNotice, setPunchReminderNotice] = useState(null);
  /** 日別勤怠・API 由来の打刻リマインド（通知 DB が空でも表示可能） */
  const [punchReminderFromApi, setPunchReminderFromApi] = useState(false);
  /** 本日のスケジュール登録有無 */
  const [hasScheduleToday, setHasScheduleToday] = useState(true);
  /** 出社ボタンを有効にするか（退社後・夜勤終了後はグレーアウト） */
  const [canClockIn, setCanClockIn] = useState(true);
  /** 本日シフト開始（HH:MM）— 遅刻判定用 */
  const [scheduleStart, setScheduleStart] = useState(null);
  /** 昨日の退社打刻が未記録（オープンレコードが前日以前） */
  const [hasYesterdayPunchMissing, setHasYesterdayPunchMissing] = useState(false);
  // 当日残業モーダル
  const [showOvertimePrompt, setShowOvertimePrompt] = useState(false);
  const [overtimeCheckResult, setOvertimeCheckResult] = useState(null);

  // 昨日打刻忘れモーダル
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotInfo, setForgotInfo] = useState(null); // { record_date, record_date_label, schedule_end_time, shift_name }

  // 昨日残業入力フォーム
  const [showOvertimeInput, setShowOvertimeInput] = useState(false);
  const [overtimeStart, setOvertimeStart] = useState("");
  const [overtimeEnd, setOvertimeEnd] = useState("");

  /** 管理者アカウントで開いた場合 true（打刻 API は 403 のため、案内表示して出社・退社を押下不可にする） */
  const [isAdminUser, setIsAdminUser] = useState(false);
  /** 打刻忘れリマインド時: 実際の出勤時刻（任意）。入力時は出社 API に渡す */
  const [actualStartTime, setActualStartTime] = useState("");

  const getFormattedDate = (date) => {
    if (!date) return "";
    const days = ["日", "月", "火", "水", "木", "金", "土"];
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}年/${m}月/${d}日 (${days[date.getDay()]})`;
  };

  const formatTimeWithSeconds = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const sec = String(date.getSeconds()).padStart(2, '0');
    return `${h}:${min}:${sec}`;
  };
  
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const empRes = await api.get("/employee/");
        if (!cancelled) setEmployee(empRes.data);
      } catch (error) {
        if (!cancelled && error.response?.status === 401) {
          setMessage(MESSAGES.LOGIN_EXPIRED);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

 
 {/*}
  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    const fetchStatus = async () => {
      try {
        const res = await api.get(API.WORKTIME_STATUS);
        const isWorking = res.data.working;
        const yesterdayMissing = res.data.has_yesterday_punch_missing === true;
        setIsClockedIn(isWorking);
        setHasScheduleToday(res.data.has_schedule_today !== false);
        setCanClockIn(res.data.can_clock_in !== false);
        setHasYesterdayPunchMissing(yesterdayMissing);
        if (isWorking) {
          setStatusMessage(STATUS.WORKING);
        } else if (yesterdayMissing) {
          setStatusMessage("打刻確認が必要");
        } else {
          setStatusMessage(STATUS.LEFT);
        }
        setIsAdminUser(false);
      } catch (err) {
        if (err.response?.status === 403) {
          setIsAdminUser(true);
        } else if (err.response?.status === 401) {
          setStatusMessage(MESSAGES.LOGIN_EXPIRED);
        }
      }
    };
    fetchStatus();
    return () => clearInterval(timer);
  }, []);
*/}

  
  const fetchWorktimeStatus = useCallback(async () => {
      try {
          
          const res = await api.get(`${API.WORKTIME_STATUS}?t=${Date.now()}`);
          const isWorking = res.data.working;
          const yesterdayMissing = res.data.has_yesterday_punch_missing === true;
          
          setIsClockedIn(isWorking);
          setHasScheduleToday(res.data.has_schedule_today !== false);
          setCanClockIn(res.data.can_clock_in !== false);
          setScheduleStart(res.data.schedule_start || null);
          setHasYesterdayPunchMissing(yesterdayMissing);
          
          if (isWorking) {
              setStatusMessage(STATUS.WORKING);
          } else if (yesterdayMissing) {
              setStatusMessage("打刻確認が必要");
          } else {
              setStatusMessage(STATUS.LEFT);
          }
          setIsAdminUser(false);
      } catch (err) {
          if (err.response?.status === 403) {
              setIsAdminUser(true);
          } else if (err.response?.status === 401) {
              setStatusMessage(MESSAGES.LOGIN_EXPIRED);
          }
      }
  }, []); 

  
  useEffect(() => {
      const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
      fetchWorktimeStatus();
      return () => clearInterval(timer);
  }, [fetchWorktimeStatus]);

  // 遅刻申請を送信して戻ってきたら、そのまま出社打刻を行う。
  // 打刻時刻は申請画面へ進む前にボタンを押した時刻（latePunch）を使う。
  useEffect(() => {
      const latePunch = new URLSearchParams(location.search).get("latePunch");
      if (!latePunch || isClockedIn) return;
      // 同じ打刻を二重に実行しないよう、先にURLからパラメータを消す
      navigate("/worktime", { replace: true });
      handleClockIn(latePunch);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isClockedIn]);

 
  useEffect(() => {
      const handleFocus = () => {
          fetchWorktimeStatus();
      };
      const handleVisibility = () => {
          if (!document.hidden) fetchWorktimeStatus();
      };
      const handlePageShow = (e) => {
          if (e.persisted) fetchWorktimeStatus();
      };

      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('pageshow', handlePageShow);
      return () => {
          window.removeEventListener('focus', handleFocus);
          document.removeEventListener('visibilitychange', handleVisibility);
          window.removeEventListener('pageshow', handlePageShow);
      };
  }, [fetchWorktimeStatus]);

  const punchReminderApiSaysShow = (data) => {
    if (!data || typeof data !== "object") return false;
    const t = (k) => data[k] === true;
    return (
      t("show") ||
      t("show_reminder") ||
      t("needs_reminder") ||
      t("forgot_punch") ||
      t("remind") ||
      t("remind_today") ||
      t("should_remind")
    );
  };
  
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let notice = null;
      let fromApi = false;
      try {
        const res = await api.get("/notifications/");
        const list = res.data?.results ?? res.data ?? [];
        const arr = Array.isArray(list) ? list : [];
        notice = arr.find((n) => n.title === PUNCH_REMINDER_TITLE) || null;
      } catch (_) {
        notice = null;
      }
      try {
        const pr = await api.get("/worktime/my/punch-reminder/");
        fromApi = punchReminderApiSaysShow(pr.data);
      } catch (_) {
        fromApi = false;
      }
      if (!cancelled) {
        setPunchReminderNotice(notice);
        setPunchReminderFromApi(fromApi);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isClockedIn]);

  const showPunchReminderBanner =
    (punchReminderNotice || punchReminderFromApi) && !isAdminUser && !isClockedIn;

  const punchReminderTodayLabel = `${currentDateTime.getMonth() + 1}月${currentDateTime.getDate()}日`;

  // 現在地(GPS)を取得。失敗時は座標なしで継続。
  const getCurrentPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({});
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    });

  // 勤務地外打刻時の理由入力フローを内包したCLOCK_IN呼び出し。
  // require_reason のレスポンスを受けたらユーザーに理由を入力してもらい、理由付きで再送する。
  const postClockInWithRemoteReasonPrompt = async (body) => {
    try {
      return await api.post(API.CLOCK_IN, body);
    } catch (err) {
      const d = err.response?.data;
      if (d?.require_reason) {
        const reason = window.prompt(
          (d.message || "勤務地外での打刻です。") + "\n（例：客先訪問、出張、在宅勤務 など）"
        );
        if (!reason || !reason.trim()) {
          const e = new Error("理由が未入力のため、打刻を中止しました。");
          e.userCanceled = true;
          throw e;
        }
        return await api.post(API.CLOCK_IN, { ...body, remote_reason: reason.trim() });
      }
      throw err;
    }
  };

  const formatStampedAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${y}年${m}月${day}日 ${h}:${min} に打刻されました`;
  };

  const handleClockIn = async (overrideStartTime = "") => {
    setMessageLink(null);

    // 昨日の退社打刻が未記録: メッセージとリンクを表示してAPIは呼ばない
    if (hasYesterdayPunchMissing) {
      setMessage("昨日の退社打刻が未記録です。実績画面で修正をお願いします。修正後に本日の打刻ができます。");
      setMessageLink({ text: "実績画面へ", to: "/worktime/record" });
      return;
    }

    if (scheduleStart && isLateForClockIn(scheduleStart)) {
      const alreadyApplied = await hasLateApplicationToday(api);
      if (!alreadyApplied) {
        const planned = formatNowHm();
        // 早退と同じ2段確認。始業時刻を過ぎていても、上司の指示で開始時刻が変わった等で
        // 遅刻ではない場合があるため、「遅刻ではない」を選べるようにする。
        const isLate = window.confirm(
          `始業時刻（${scheduleStart}）を過ぎています。遅刻ですか？\n\n` +
            "「OK」: 遅刻（申請画面へ進みます）\n" +
            "「キャンセル」: 遅刻ではない"
        );
        if (isLate) {
          // 打刻は遅刻申請の送信後に行う（申請しないまま出社打刻だけ残るのを防ぐ）
          const q = new URLSearchParams({
            punchCategory: "LATE_IN",
            scheduleStart,
            plannedStart: (actualStartTime && actualStartTime.trim()) || planned,
            fromPunch: "1",
            returnTo: "/worktime",
          });
          navigate(`/worktime/application?${q.toString()}`);
          return;
        }
        const startNow = window.confirm(
          `業務開始ですか？\n\n「OK」: この時刻（${planned}）で出社打刻\n「キャンセル」: 中止`
        );
        if (!startNow) return;
        // 遅刻ではないので申請せず、そのまま通常の出社打刻に進む（下の処理へ）
      }
    }

    setLoading(true);
    try {
      const pos = await getCurrentPosition();
      const body = { ...pos };
      // overrideStartTime: 遅刻申請の送信後に呼ばれたとき、ボタンを押した時刻で打刻する
      const manualStart = (overrideStartTime || actualStartTime || "").trim();
      if (manualStart) {
        body.actual_start_time = manualStart;
      }
      const res = await postClockInWithRemoteReasonPrompt(body);
      // スケジュール未登録の場合: 打刻成功後にスケジュール登録を促すメッセージを表示
      if (!hasScheduleToday) {
        setMessage("本日のスケジュールが登録されていません。スケジュール画面で登録をお願いします。");
        setMessageLink({ text: "スケジュール画面へ", to: "/schedule" });
      } else {
        setMessage(`出社時刻: ${formatTimeWithSeconds(res.data.start_time)}`);
      }
      setIsClockedIn(true);
      setCanClockIn(false);
      setStatusMessage(STATUS.WORKING);
      setActualStartTime("");
      try {
        const pr = await api.get("/worktime/my/punch-reminder/");
        setPunchReminderFromApi(punchReminderApiSaysShow(pr.data));
      } catch (_) {
        setPunchReminderFromApi(false);
      }
    } catch (err) {
      if (err?.userCanceled) {
        setMessage(err.message);
      } else {
        const data = err.response?.data || {};
        const msg = data.message || MESSAGES.ERROR_GENERIC;
        if (err.response?.status === 400 && (msg.includes("既に") || msg.includes("完了"))) {
          const stamped = formatStampedAt(data.stamped_at);
          setMessage(stamped || msg);
        } else {
          setMessage(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setMessageLink(null);
    setLoading(true);
    try {
      const checkRes = await api.post(API.CHECK_OVERTIME);
      setOvertimeCheckResult(checkRes.data);

      if (checkRes.data.forgot_previous_day) {
        // 昨日（過去日）の退社打刻忘れ
        setForgotInfo({
          record_date: checkRes.data.record_date,
          record_date_label: checkRes.data.record_date_label,
          schedule_end_time: checkRes.data.schedule_end_time,
          shift_name: checkRes.data.shift_name,
        });
        setShowForgotModal(true);
        setLoading(false);
      } else if (checkRes.data.required_prompt) {
        // 当日の残業確認
        setShowOvertimePrompt(true);
        setLoading(false);
      } else {
        await finalizeClockOut({});
      }
    } catch (err) {
      setMessage(err.response?.data?.message || MESSAGES.ERROR_GENERIC);
      setMessageLink(null);
      setLoading(false);
    }
  };

  // 昨日忘れ：私用（シフト終了時刻で退社）
  const handleForgotPrivate = async () => {
    setShowForgotModal(false);
    await finalizeClockOut({ use_schedule_end: true });
  };

  // 昨日忘れ：残業入力へ進む
  const handleForgotOvertime = () => {
    setShowForgotModal(false);
    setOvertimeStart(forgotInfo?.schedule_end_time || "");
    setOvertimeEnd("");
    setShowOvertimeInput(true);
  };

  // 残業時間を確定して退社
  const handleOvertimeSubmit = async () => {
    if (!overtimeEnd) {
      setMessage("残業終了時刻を入力してください。");
      return;
    }
    setShowOvertimeInput(false);
    await finalizeClockOut({ overtime_end: overtimeEnd, is_overtime: true });
  };

  const finalizeClockOut = async ({ is_overtime = false, use_schedule_end = false, overtime_end = null }) => {
    setLoading(true);
    setShowOvertimePrompt(false);
    setShowForgotModal(false);
    setShowOvertimeInput(false);
    try {
      const pos = await getCurrentPosition();
      const body = { is_overtime, ...pos };
      if (use_schedule_end) body.use_schedule_end = true;
      if (overtime_end) body.overtime_end = overtime_end;

      const res = await api.post(API.CLOCK_OUT, body);
      const timeStr = formatTimeWithSeconds(res.data.end_time);
      if (use_schedule_end) {
        setMessage(`退社時刻: ${timeStr}（シフト終了時刻で記録）`);
      } else if (overtime_end) {
        setMessage(`退社時刻: ${timeStr}（残業として記録）`);
      } else {
        setMessage(is_overtime ? `退社時刻: ${timeStr}（残業として記録）` : `退社時刻: ${timeStr}`);
      }
      setIsClockedIn(false);
      setStatusMessage(STATUS.LEFT);
      // 退社後は出社ボタンをグレーアウト（次のシフト開始1時間前まで）
      setCanClockIn(false);
      try {
        const pr = await api.get("/worktime/my/punch-reminder/");
        setPunchReminderFromApi(punchReminderApiSaysShow(pr.data));
      } catch (_) {
        /* ignore */
      }
      // 上司承認済みの残業がある日は、退勤後に残業申請（本申請）画面へ誘導する
      if (!use_schedule_end) {
        try {
          const ov = await api.get("/api/employee/my-overtime/supervisor-pending/");
          const todayStr = new Date().toISOString().slice(0, 10);
          const approved = Array.isArray(ov.data?.approved) ? ov.data.approved : [];
          if (approved.some((a) => a.date === todayStr)) {
            navigate("/worktime/application");
          }
        } catch (_) {
          /* ignore */
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setMessage(MESSAGES.LOGIN_EXPIRED);
      } else if (err.request && !err.response) {
        setMessage(MESSAGES.NETWORK_ERROR);
      } else {
        setMessage(err.response?.data?.message || MESSAGES.ERROR_GENERIC);
      }
      setMessageLink(null);
    } finally {
      setLoading(false);
    }
  };

  if (!employee) {
    return (
      <div className="employee-time-loading">
        <h2>データをロードしています...</h2>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchWorktimeStatus}>
      <div className="employee-time-page">
        <h2 className="employee-time-title">打刻機能</h2>
        <h3 className="employee-time-date">{getFormattedDate(currentDateTime)}</h3>

        {/* 管理者アカウントで開いた場合は打刻不可である旨を表示し、出社・退社を押下不可 */}
        {isAdminUser && (
          <div className="employee-time-no-schedule employee-time-admin-notice" role="alert">
            <p className="employee-time-no-schedule-text">
              管理者アカウントでは打刻機能はご利用いただけません。
            </p>
            <p className="employee-time-admin-notice-sub">
              社員の打刻忘れは「<Link to="/admin/worktime-daily" className="employee-time-admin-link">打刻忘れ管理</Link>」画面で実打刻を修正してください。
            </p>
          </div>
        )}

        {/* 昨日打刻忘れ警告バナー */}
        {hasYesterdayPunchMissing && !isAdminUser && (
          <div className="employee-time-no-schedule employee-time-yesterday-missing" role="alert">
            <p className="employee-time-no-schedule-text">
              昨日の退社打刻が未記録です。出社ボタンを押すと案内が表示されます。
            </p>
          </div>
        )}

        <p className="employee-time-message">
          {message}
          {messageLink && (
            <>
              {" "}
              <Link to={messageLink.to} className="employee-time-message-link">
                {messageLink.text}
              </Link>
            </>
          )}
        </p>

        {/* 当日残業確認モーダル */}
        {showOvertimePrompt && (
          <div className="employee-time-overtime-overlay" role="dialog" aria-labelledby="overtime-title" aria-modal="true">
            <div className="employee-time-overtime-modal">
              <h4 id="overtime-title" className="employee-time-overtime-title">残業確認</h4>
              <p className="employee-time-overtime-text">
                規定の退社時刻から15分以上経過しています。<br /><br />
                <strong>残業</strong>として記録しますか、それとも<strong>私用</strong>ですか？
              </p>
              <div className="employee-time-overtime-actions">
                <button type="button" className="employee-time-overtime-btn employee-time-overtime-btn-overtime" onClick={() => finalizeClockOut({ is_overtime: true })}>残業</button>
                <button type="button" className="employee-time-overtime-btn employee-time-overtime-btn-private" onClick={() => finalizeClockOut({ is_overtime: false })}>私用</button>
              </div>
            </div>
          </div>
        )}

        {/* 昨日打刻忘れモーダル */}
        {showForgotModal && forgotInfo && (
          <div className="employee-time-overtime-overlay" role="dialog" aria-labelledby="forgot-modal-title" aria-modal="true">
            <div className="employee-time-overtime-modal employee-time-forgot-modal">
              <h4 id="forgot-modal-title" className="employee-time-overtime-title employee-time-forgot-title">
                ⚠️ 退社打刻が未記録です
              </h4>
              <p className="employee-time-overtime-text">
                <strong>{forgotInfo.record_date_label}</strong> の退社打刻が記録されていません。<br /><br />
                {forgotInfo.schedule_end_time && (
                  <>
                    シフト予定終了時刻：<strong>{forgotInfo.schedule_end_time}</strong>
                    {forgotInfo.shift_name ? `（${forgotInfo.shift_name}）` : ""}
                    <br /><br />
                  </>
                )}
                昨日は<strong>残業</strong>しましたか？
              </p>
              <div className="employee-time-overtime-actions">
                <button
                  type="button"
                  className="employee-time-overtime-btn employee-time-overtime-btn-overtime"
                  onClick={handleForgotOvertime}
                >
                  残業した
                </button>
                <button
                  type="button"
                  className="employee-time-overtime-btn employee-time-overtime-btn-private"
                  onClick={handleForgotPrivate}
                >
                  私用（予定終了時刻で退社）
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 残業時刻入力フォーム（昨日忘れ時） */}
        {showOvertimeInput && forgotInfo && (
          <div className="employee-time-overtime-overlay" role="dialog" aria-labelledby="overtime-input-title" aria-modal="true">
            <div className="employee-time-overtime-modal employee-time-forgot-modal">
              <h4 id="overtime-input-title" className="employee-time-overtime-title">
                残業時刻の入力
              </h4>
              <p className="employee-time-overtime-text">
                <strong>{forgotInfo.record_date_label}</strong> の残業時刻を入力してください。
              </p>
              <div className="employee-time-overtime-input-row">
                <label className="employee-time-overtime-label">
                  残業開始時刻
                  <input
                    type="time"
                    className="employee-time-overtime-time-input"
                    value={overtimeStart}
                    onChange={(e) => setOvertimeStart(e.target.value)}
                  />
                </label>
              </div>
              <div className="employee-time-overtime-input-row">
                <label className="employee-time-overtime-label">
                  残業終了時刻（退社時刻）
                  <input
                    type="time"
                    className="employee-time-overtime-time-input"
                    value={overtimeEnd}
                    onChange={(e) => setOvertimeEnd(e.target.value)}
                  />
                </label>
              </div>
              <div className="employee-time-overtime-actions" style={{ marginTop: 20 }}>
                <button
                  type="button"
                  className="employee-time-overtime-btn employee-time-overtime-btn-overtime"
                  onClick={handleOvertimeSubmit}
                  disabled={!overtimeEnd}
                >
                  確定
                </button>
                <button
                  type="button"
                  className="employee-time-overtime-btn employee-time-overtime-btn-private"
                  onClick={() => {
                    setShowOvertimeInput(false);
                    setShowForgotModal(true);
                  }}
                >
                  戻る
                </button>
              </div>
            </div>
          </div>
        )}
        {/*}
        <div className="employee-time-buttons">
          <button
            type="button"
            className="employee-time-btn employee-time-btn-clock-in"
            onClick={handleClockIn}
            disabled={loading || isClockedIn || isAdminUser || !canClockIn}
            aria-label="出社打刻"
          >
            出社
          </button>
        </div>
        <div className="employee-time-buttons">
          <button
            type="button"
            className="employee-time-btn employee-time-btn-clock-out"
            onClick={handleClockOut}
            disabled={loading || !isClockedIn || isAdminUser}
            aria-label="退社打刻"
          >
            退社
          </button>
        </div>
        */}
        <div className="employee-time-buttons">
          <Link
            to="/worktime/record"
            className={`employee-time-btn employee-time-btn-record${isAdminUser ? " employee-time-btn-record-disabled" : ""}`}
            aria-label="実績一覧を開く"
            onClick={(e) => {
              if (isAdminUser) e.preventDefault();
            }}
          >
            実績
          </Link>
        </div>
        {/*
        <div className="employee-time-buttond">
            <Link
              to="/worktime/application"
              className={`employee-time-btn employee-time-btn-application${isAdminUser ? " employee-time-btn-application-disabled" : ""}`}
              aria-label="申請一覧を開く"
              onClick={(e) => {
                if (isAdminUser) e.preventDefault();
              }}
            >
              申請
            </Link>
        </div>
        */}
        {showPunchReminderBanner && (
          <div className="employee-time-reminder-banner employee-time-reminder-banner-bottom" role="alert">
            <strong>📩 打刻のお願い</strong>
            <p className="employee-time-reminder-text">
              本日（{punchReminderTodayLabel}）の出勤・退勤打刻が未記録です。お手数ですが上司に連絡してください。必要に応じて実際の出勤時刻を入力のうえ出社打刻してください。
            </p>
            <div className="employee-time-actual-start-wrap">
              <label className="employee-time-actual-start-label">
                実際の出勤時刻（任意）
                <input
                  type="time"
                  className="employee-time-actual-start-input"
                  value={actualStartTime}
                  onChange={(e) => setActualStartTime(e.target.value)}
                  aria-label="実際の出勤時刻"
                />
              </label>
              <p className="employee-time-actual-start-hint">
                入力するとその時刻で記録されます。未入力の場合は「今」の時刻で打刻されます。
              </p>
            </div>
          </div>
        )}

        <div className="employee-time-back-wrap">
          <Link to="/card_back" className="employee-time-back-btn">戻る</Link>
        </div>

      </div>
    </PullToRefresh>
  );
};

export default EmployeeTime;
